import { useState, useEffect } from "react";
import { Button } from "../button";
import { Input } from "../input";
import { Label } from "../label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../select";
import { Checkbox } from "../checkbox";
import { Slider } from "../slider";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../collapsible";
import { ChevronDown, X } from "lucide-react";

const FilterPanel = ({ onFilter }) => {
  const [selectedFilters, setSelectedFilters] = useState({
    category: "", // Changed to string for single select
    location: "",
    type: "",
    experience: "",
    minSalary: "",
    maxSalary: "",
    tag: "",
    days: "",
  });
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [tags, setTags] = useState([]);
  const [salaryRange, setSalaryRange] = useState([0, 100000]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      // Use client instead of fetch to respect baseURL
      const res = await import("../../../api/client").then(m => m.default.get("/jobs/filters"));
      const data = res.data;
      setCategories(data.categories || []);
      setLocations(data.locations || []);
      setTags(data.tags || []);
      setSalaryRange([data.salary?.min || 0, data.salary?.max || 100000]);
    } catch (err) {
      console.error("Failed to fetch filter options:", err);
      // Fallback to sample data
      setCategories([
        { title: "Technology", count: 10 },
        { title: "Marketing", count: 5 },
        { title: "Finance", count: 8 },
        { title: "Healthcare", count: 6 },
      ]);
      setLocations(["New York", "San Francisco", "London"]);
      setTags(["React", "Node.js", "Python"]);
      setSalaryRange([0, 100000]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSelectedFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Removed handleCategoryChange as category is now single select

  const clearFilters = () => {
    setSelectedFilters({
      category: "",
      location: "",
      type: "",
      experience: "",
      minSalary: "",
      maxSalary: "",
      tag: "",
      days: "",
    });
  };

  const applyFilters = () => {
    onFilter(selectedFilters);
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow border border-gray-200 dark:border-gray-700">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded mb-4"></div>
          <div className="h-10 bg-gray-300 dark:bg-gray-600 rounded mb-4"></div>
          <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded mb-4"></div>
          <div className="h-10 bg-gray-300 dark:bg-gray-600 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow border border-gray-200 dark:border-gray-700 sticky top-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Filters
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={clearFilters}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <X className="w-4 h-4 mr-1" />
          Clear
        </Button>
      </div>

      <div className="space-y-6">
        {/* Category - Single-select dropdown */}
        <div>
          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Category
          </Label>
          <Select
            value={selectedFilters.category || "all"}
            onValueChange={(value) =>
              handleChange("category", value === "all" ? "" : value)
            }
          >
            <SelectTrigger className="mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-indigo-500">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700">
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.title} value={cat.title}>
                  {cat.title} ({cat.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Location */}
        <div>
          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Location
          </Label>
          <Select
            value={selectedFilters.location || "all"}
            onValueChange={(value) =>
              handleChange("location", value === "all" ? "" : value)
            }
          >
            <SelectTrigger className="mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-indigo-500">
              <SelectValue placeholder="All Locations" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700">
              <SelectItem value="all">All Locations</SelectItem>
              {locations.map((loc) => (
                <SelectItem key={loc} value={loc}>
                  {loc}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Job Type */}
        <div>
          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Job Type
          </Label>
          <Select
            value={selectedFilters.type || "all"}
            onValueChange={(value) =>
              handleChange("type", value === "all" ? "" : value)
            }
          >
            <SelectTrigger className="mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-indigo-500">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700">
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="FULL_TIME">Full Time</SelectItem>
              <SelectItem value="PART_TIME">Part Time</SelectItem>
              <SelectItem value="CONTRACT">Contract</SelectItem>
              <SelectItem value="INTERNSHIP">Internship</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Experience */}
        <div>
          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Experience
          </Label>
          <Select
            value={selectedFilters.experience || "all"}
            onValueChange={(value) =>
              handleChange("experience", value === "all" ? "" : value)
            }
          >
            <SelectTrigger className="mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-indigo-500">
              <SelectValue placeholder="All Levels" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700">
              <SelectItem value="all">All Levels</SelectItem>
              <SelectItem value="ENTRY">Entry</SelectItem>
              <SelectItem value="MID">Mid</SelectItem>
              <SelectItem value="SENIOR">Senior</SelectItem>
              <SelectItem value="EXECUTIVE">Executive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Salary Range */}
        <Collapsible>
          <CollapsibleTrigger className="flex items-center justify-between w-full text-left">
            <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Salary Range
            </Label>
            <ChevronDown className="w-4 h-4" />
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2">
            <div className="px-2">
              <Slider
                value={[
                  selectedFilters.minSalary || salaryRange[0],
                  selectedFilters.maxSalary || salaryRange[1],
                ]}
                onValueChange={([min, max]) => {
                  handleChange("minSalary", min);
                  handleChange("maxSalary", max);
                }}
                max={salaryRange[1]}
                min={salaryRange[0]}
                step={1000}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                <span>{selectedFilters.minSalary || salaryRange[0]} ETB</span>
                <span>{selectedFilters.maxSalary || salaryRange[1]} ETB</span>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Tags */}
        <div>
          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Tags
          </Label>
          <div className="mt-2 space-y-2 max-h-32 overflow-y-auto">
            {tags.slice(0, 10).map((tag) => (
              <div key={tag} className="flex items-center space-x-2">
                <Checkbox
                  id={tag}
                  checked={selectedFilters.tag === tag}
                  onCheckedChange={(checked) =>
                    handleChange("tag", checked ? tag : "")
                  }
                />
                <Label
                  htmlFor={tag}
                  className="text-sm text-gray-600 dark:text-gray-400"
                >
                  {tag}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Posted Within */}
        <div>
          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Posted Within
          </Label>
          <Select
            value={selectedFilters.days || "all"}
            onValueChange={(value) =>
              handleChange("days", value === "all" ? "" : value)
            }
          >
            <SelectTrigger className="mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 focus:ring-2 focus:ring-indigo-500">
              <SelectValue placeholder="Any time" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700">
              <SelectItem value="all">Any time</SelectItem>
              <SelectItem value="1">Last 24 hours</SelectItem>
              <SelectItem value="7">Last week</SelectItem>
              <SelectItem value="30">Last month</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Apply Filters Button */}
      <div className="mt-6">
        <Button
          onClick={applyFilters}
          className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
        >
          Apply Filters
        </Button>
      </div>
    </div>
  );
};

export default FilterPanel;
